import React, { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Spinner";
import MetricTrendChart from "../../charts/MetricTrendChart";
import SpirometryResultRow from "../rows/SpirometryResultRow";
import { useSpirometryResults } from "../../../hooks/results/useSpirometryResults";
import { useTranslation } from "react-i18next";
import { getValidatedMeasurementValue } from "../../../utils/results/validatedRanges";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";
import {
  formatDayMonth,
  getLocalDayKeyMs,
} from "../../../utils/results/recordingIdTime";
import { SHOW_FULL_RESULTS } from "../../../config/results";

const SPIROMETRY_TREND_METRICS = {
  FEV1: {
    valueKey: "fev1",
    rangeKey: "fev1",
    labelKey: "results.spiro.fev1",
    labelFallback: "FEV1",
    averageKey: "results.spiro.fev1Average",
    averageFallback: "FEV1 (Avg)",
    unit: "L",
    decimals: 2,
  },
  FVC: {
    valueKey: "fvc",
    rangeKey: "fvc",
    labelKey: "results.spiro.fvc",
    labelFallback: "FVC",
    averageKey: "results.spiro.fvcAverage",
    averageFallback: "FVC (Avg)",
    unit: "L",
    decimals: 2,
  },
  FEV1_FVC: {
    valueKey: "fev1FvcRatio",
    rangeKey: "fev1FvcRatio",
    labelKey: "results.spiro.fev1FvcRatio",
    labelFallback: "FEV1/FVC",
    averageKey: "results.spiro.fev1FvcRatioAverage",
    averageFallback: "FEV1/FVC (Avg)",
    unit: "",
    decimals: 2,
  },
  PEF: {
    valueKey: "pef",
    rangeKey: "pef",
    labelKey: "results.spiro.pef",
    labelFallback: "PEF",
    averageKey: "results.spiro.pefAverage",
    averageFallback: "PEF (Avg)",
    unit: "L/min",
    decimals: 0,
  },
  RR: {
    valueKey: "rr",
    rangeKey: "rr",
    labelKey: "results.cardio.rr",
    labelFallback: "RR",
    averageKey: "results.cardio.rrAverage",
    averageFallback: "RR (Avg)",
    unit: "bpm",
    decimals: 0,
  },
};

export default memo(function SpirometryResults({ db, uid }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [metric, setMetric] = useState("FEV1");
  const metricConfig =
    SPIROMETRY_TREND_METRICS[metric] || SPIROMETRY_TREND_METRICS.FEV1;
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const resetKey = `${uid || ""}:spiro`;
  const { rows, loading, loadingMore, hasMore, error } = useSpirometryResults(
    db,
    uid,
    {
      pageSize,
    },
  );
  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useCallback(
    (row) => {
      const variant = "RecordingSpirometry";
      const docid = row?.id;
      if (!docid) return;

      navigate(
        `/result?variant=${encodeURIComponent(variant)}&docid=${encodeURIComponent(docid)}`,
      );
    },
    [navigate],
  );

  // Daily averages + min/max per selected metric (same logic as your pulse daily aggregation)
  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return { labels: [], xValues: [], values: [], minMax: [], counts: [] };
    }

    const chron = [...rows].reverse();

    const samples = [];
    for (const r of chron) {
      const ms = r?.timestamp ? Date.parse(r.timestamp) : NaN;
      if (!Number.isFinite(ms)) continue;

      const raw = getValidatedMeasurementValue(
        metricConfig.rangeKey,
        r?.[metricConfig.valueKey],
      );

      // IMPORTANT: don't coerce null/""/"--" into 0
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;

      samples.push({ ms, v: raw });
    }

    const byDay = new Map(); // dayStartMs -> { sum,n,min,max }
    for (const s of samples) {
      const dayStartMs = getLocalDayKeyMs(s.ms, i18n.language);
      if (!Number.isFinite(dayStartMs)) continue;

      const cur = byDay.get(dayStartMs) || { sum: 0, n: 0, min: s.v, max: s.v };
      cur.sum += s.v;
      cur.n += 1;
      cur.min = Math.min(cur.min, s.v);
      cur.max = Math.max(cur.max, s.v);
      byDay.set(dayStartMs, cur);
    }

    const days = Array.from(byDay.entries())
      .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
      .sort((a, b) => a.dayStartMs - b.dayStartMs);

    const xValues = days.map((d) => d.dayStartMs);
    const values = days.map((d) => d.sum / d.n);
    const minMax = days.map((d) => ({ min: d.min, max: d.max }));
    const counts = days.map((d) => d.n);
    const labels = xValues.map((ms) => formatDayMonth(ms, i18n.language));

    return { labels, xValues, values, minMax, counts };
  }, [rows, metricConfig, i18n.language]);

  if (!uid || loading) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="muted-text">
        {t("results.failedLoad", "Failed to load results.")}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="muted-text">
        {t("results.emptyRecordPrompt", "No data yet")}
      </div>
    );
  }

  const yUnit = metricConfig.unit;
  const yDecimals = metricConfig.decimals;
  const lineLabel = t(metricConfig.averageKey, metricConfig.averageFallback);

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-2">
      {SHOW_FULL_RESULTS && (
        <>
          {/* Metric toggle (only affects chart) */}
          <div className="w-full flex flex-wrap gap-2 pt-2">
            {Object.entries(SPIROMETRY_TREND_METRICS).map(([key, config]) => (
              <button
                key={key}
                type="button"
                className={[
                  "px-2 py-1 rounded-xl text-sm",
                  metric === key
                    ? "bg-primary text-greyscale-1"
                    : "bg-greyscale-1 text-primary",
                ].join(" ")}
                onClick={() => setMetric(key)}
              >
                {t(config.labelKey, config.labelFallback)}
              </button>
            ))}
          </div>

          {/* Trend chart */}
          <MetricTrendChart
            height={190}
            labels={trend?.labels}
            values={trend?.values}
            xValues={trend?.xValues}
            xTickFormat="dd/MM"
            lineLabel={lineLabel}
            yUnit={yUnit}
            yDecimals={yDecimals}
            showMinMaxBars={true}
            minMax={trend?.minMax}
            counts={trend?.counts}
            showTrendLine={true}
          />
        </>
      )}
      {/* scrolling list */}
      <div
        aria-label="spirometry results list"
        className="w-full flex-1 min-h-0 overflow-y-auto"
        onScroll={handleScroll}
      >
        {rows.map((r, idx) => (
          <SpirometryResultRow
            key={r.id ?? idx}
            time={r.time}
            date={r.date}
            fev1={r.fev1}
            fvc={r.fvc}
            fev1FvcRatio={r.fev1FvcRatio}
            pef={r.pef}
            rr={r.rr}
            signalQuality={r.signalQuality}
            spirometryQuality={r.spirometryQuality}
            onClick={() => onRowClick(r)}
          />
        ))}
        {loadingMore ? <Spinner size={24} className="py-4" /> : null}
      </div>
    </div>
  );
});
